import { Contest, ContestEntry, JoinedContest } from "interfaces/leaderboard/contest";
import { createAsyncThunk, createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "state/store";
import {
  getBetsPath,
  getContestEntryPath,
  getContestsPath,
  getJoinedContestsPath,
  integratedGetContestEntry,
  integratedGetJoinedContests,
  integratedMyBets
} from "utils/backend-path-builders";
import { getRequest } from "utils/httpClient";
import { CONTEST_ENTRY_STATUS } from "utils/constants.ts";
import { getIntegratedAppState } from "./integrated-app-slice";
import { Bet } from "interfaces/bet.ts";
import { getGroupState } from "./group-slice";

export type LoadingState = "idle" | "loading" | "succeeded" | "failed";

export interface FullContestEntryDetails {
  bets: Bet[];
  loadingState: LoadingState;
  error: string | null;
}

export interface ContestsState {
  contests: Contest[];
  joinedContests: JoinedContest[];
  loadingUserContests: boolean;
  loadingContests: boolean;
  error: string | null;
  entryIdToContestId: Record<string, string>;
  contestIdToEntries: Record<string, ContestEntry[]>;
  fullContestEntryDetails: Record<string, FullContestEntryDetails>;
  entryRefreshLoadingById: Record<string, LoadingState>;
  activeContest?: Contest;
  contestsStale: boolean;
  userContestsStale: boolean;
  newContestEntry?: RecentlyJoinedContestEntry;
  currentContestEntryId?: string;
  contestIdToCurrentEntryId: Record<string, string>;
}

export interface RecentlyJoinedContestEntry {
  id: string;
  balanceHasBeenAnimated: boolean;
  bannerHasBeenShown: boolean;
}

export const initialState: ContestsState = {
  contests: [],
  joinedContests: [],
  error: null,
  loadingUserContests: true,
  loadingContests: true,
  entryIdToContestId: {},
  contestIdToEntries: {},
  fullContestEntryDetails: {},
  entryRefreshLoadingById: {},
  contestsStale: true,
  userContestsStale: true,
  currentContestEntryId: undefined,
  contestIdToCurrentEntryId: {},
};

export const fetchContests = createAsyncThunk<
  Contest[],
  string,
  { rejectValue: string }
>("contests/fetchContests", async (groupId, { rejectWithValue }) => {
  const contestsPath = getContestsPath(groupId);

  try {
    return await getRequest(contestsPath, { 
      skipIntegrationApi: true,
      queryParams: { 
        includeDeleted: 'true',
        page_size: '30'
      }
    });
  } catch (error) {
    console.error(`Error fetching contests`, error);
    return rejectWithValue("Failed to fetch contests");
  }
});

export const fetchUserContests = createAsyncThunk<
  JoinedContest[],
  string,
  { rejectValue: string; state: RootState }
>("contests/fetchUserContests", async (groupId, { rejectWithValue, getState }) => {
  const isIntegratedApp = getIntegratedAppState(getState()).isIntegratedApp;
  const fetchJoinedContestsPath = isIntegratedApp ? integratedGetJoinedContests() : getJoinedContestsPath(groupId);

  try {
    return await getRequest(fetchJoinedContestsPath);
  } catch (error) {
    console.error(`Error fetching contests`, error);
    return rejectWithValue("Failed to fetch contests");
  }
});

export const fetchBetsForContestEntry = createAsyncThunk<
  { contestEntryId: string; bets: Bet[] },
  string, // Argument is contestEntryId
  { rejectValue: string; state: RootState }
>("contests/fetchBetsForContestEntry", async (contestEntryId, { rejectWithValue, getState }) => {
  const state = getState();
  const isIntegratedApp = getIntegratedAppState(state).isIntegratedApp;
  const group = getGroupState(state);
  const path = isIntegratedApp ? integratedMyBets(contestEntryId) : getBetsPath(group.id, contestEntryId);

  try {
    const bets: Bet[] = await getRequest(path);
    return { contestEntryId, bets };
  } catch (error) {
    console.error(`Error fetching bets for contest entry ${contestEntryId}`, error);
    return rejectWithValue("Failed to fetch bets for contest entry");
  }
});

interface UserContestEntryResponse {
  id: string;
  contest_id: string;
  contest_name: string;
  status: string;
  created_at: string;
  unique_contestants_picked: number;
  unique_contestants_required: number;
}

// Refreshes a single contest entry from the backend. Called after a bet is
// placed so the entry's status + unique-contestant counts reflect the new bet.
// Must be dispatched alongside fetchBetsForContestEntry from every bet-placement
// path — SuccessfulBetModal gates its spinner on both completing, so omitting
// this dispatch will leave the modal spinning forever.
export const refreshUserContestEntry = createAsyncThunk<
  UserContestEntryResponse,
  string, // contestEntryId
  { rejectValue: string; state: RootState }
>("contests/refreshUserContestEntry", async (contestEntryId, { rejectWithValue, getState }) => {
  const isIntegratedApp = getIntegratedAppState(getState()).isIntegratedApp;
  const path = isIntegratedApp
    ? integratedGetContestEntry(contestEntryId)
    : getContestEntryPath(contestEntryId);

  try {
    return await getRequest(path);
  } catch (error) {
    console.error(`Error refreshing contest entry ${contestEntryId}`, error);
    return rejectWithValue("Failed to refresh contest entry");
  }
});

const slice = createSlice({
  name: "contests",
  initialState,
  reducers: {
    setContestsStale: (state, action) => {
      state.contestsStale = action.payload;
    },
    setUserContestsStale: (state, action) => {
      state.userContestsStale = action.payload;
    },
    clearContestState: () => initialState,

    setNewContestEntryState: (state, action: PayloadAction<RecentlyJoinedContestEntry>) => {
      state.newContestEntry = action.payload;
    },
    setContestCurrentEntry: (state, action: PayloadAction<{ contestId: string; entryId: string }>) => {
      state.contestIdToCurrentEntryId[action.payload.contestId] = action.payload.entryId;
    },

    // Optimistically reflects a just-placed contest pick so the contestant
    // greys out and picks-remaining decrements immediately, instead of leaving
    // a window (until fetchBetsForContestEntry/refreshUserContestEntry land)
    // where the same contestant can be picked again. The follow-up refetch
    // reconciles to the authoritative backend values.
    addOptimisticContestPick: (
      state,
      action: PayloadAction<{ contestEntryId: string; bet: Bet }>
    ) => {
      const { contestEntryId, bet } = action.payload;
      const existing = state.fullContestEntryDetails[contestEntryId];
      const prevBets = existing?.bets || [];
      if (prevBets.some((b) => b.id === bet.id)) {
        // A refetch already reflected this pick; nothing to do.
        return;
      }

      const isNewContestant = !prevBets.some(
        (b) => b.contestant_name === bet.contestant_name
      );

      state.fullContestEntryDetails[contestEntryId] = {
        bets: [...prevBets, bet],
        loadingState: "succeeded",
        error: null,
      };

      if (!isNewContestant) {
        // Re-picking the same contestant doesn't change the unique-pick count.
        return;
      }

      const contestId = state.entryIdToContestId[contestEntryId];
      const entries = contestId ? state.contestIdToEntries[contestId] : undefined;
      const idx = entries ? entries.findIndex((e) => e.id === contestEntryId) : -1;
      if (!entries || idx === -1) {
        return;
      }
      const entry = entries[idx];
      const required = entry.unique_contestants_required ?? 0;
      const picked = (entry.unique_contestants_picked ?? 0) + 1;
      entries[idx] = {
        ...entry,
        unique_contestants_picked: required ? Math.min(picked, required) : picked,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContests.pending, (state: Draft<ContestsState>) => {
        state.loadingContests = true;
        state.error = null;
      })
      .addCase(fetchContests.fulfilled, (state: Draft<ContestsState>, action) => {
        state.loadingContests = false;
        state.error = null;

        state.contests = action.payload.slice().sort((a, b) => {
          return new Date(a.end_at).getTime() - new Date(b.end_at).getTime();
        });
      })
      .addCase(fetchContests.rejected, (state: Draft<ContestsState>, action) => {
        state.loadingContests = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserContests.pending, (state: Draft<ContestsState>) => {
        state.error = null;
        state.loadingUserContests = true;
      })
      .addCase(fetchUserContests.fulfilled, (state: Draft<ContestsState>, action) => {
        state.entryIdToContestId = {};
        state.contestIdToEntries = {};

        action.payload.forEach((joinedContest) => {
          const contestId: string = joinedContest.contest_id;

          if (!state.contestIdToEntries[contestId]) {
            state.contestIdToEntries[contestId] = [];
          }

          joinedContest.entries.forEach((entry: ContestEntry) => {
            entry.status = toContestStatus(entry.status);

            state.contestIdToEntries[contestId].push(entry);

            state.entryIdToContestId[entry.id] = contestId;
          });
        });

        state.loadingUserContests = false;
        state.error = null;
        state.joinedContests = action.payload;
      })
      .addCase(fetchUserContests.rejected, (state: Draft<ContestsState>, action) => {
        state.error = action.payload as string;
        state.loadingUserContests = false;
      })
      .addCase(fetchBetsForContestEntry.pending, (state: Draft<ContestsState>, action) => {
        const contestEntryId = action.meta.arg;
        const existingDetails = state.fullContestEntryDetails[contestEntryId];
        state.fullContestEntryDetails[contestEntryId] = {
          bets: existingDetails?.bets || [],
          loadingState: "loading",
          error: null,
        };
      })
      .addCase(fetchBetsForContestEntry.fulfilled, (state: Draft<ContestsState>, action) => {
        const { contestEntryId, bets } = action.payload;
        state.fullContestEntryDetails[contestEntryId] = {
          bets: bets,
          loadingState: "succeeded",
          error: null,
        };
      })
      .addCase(fetchBetsForContestEntry.rejected, (state: Draft<ContestsState>, action) => {
        const contestEntryId = action.meta.arg;
        const existingDetails = state.fullContestEntryDetails[contestEntryId];
        state.fullContestEntryDetails[contestEntryId] = {
          bets: existingDetails?.bets || [],
          loadingState: "failed",
          error: action.payload as string,
        };
      })
      .addCase(refreshUserContestEntry.pending, (state: Draft<ContestsState>, action) => {
        state.entryRefreshLoadingById[action.meta.arg] = "loading";
      })
      .addCase(refreshUserContestEntry.fulfilled, (state: Draft<ContestsState>, action) => {
        const fresh = action.payload;
        state.entryRefreshLoadingById[fresh.id] = "succeeded";
        const contestId = state.entryIdToContestId[fresh.id];
        if (!contestId) {
          return;
        }
        const entries = state.contestIdToEntries[contestId];
        if (!entries) {
          return;
        }
        const idx = entries.findIndex(e => e.id === fresh.id);
        if (idx === -1) {
          return;
        }
        entries[idx] = {
          ...entries[idx],
          status: toContestStatus(fresh.status),
          unique_contestants_picked: fresh.unique_contestants_picked,
          unique_contestants_required: fresh.unique_contestants_required,
        };
      })
      .addCase(refreshUserContestEntry.rejected, (state: Draft<ContestsState>, action) => {
        state.entryRefreshLoadingById[action.meta.arg] = "failed";
      });
  },
});

export const {
  setContestsStale,
  setUserContestsStale,
  clearContestState,
  setNewContestEntryState,
  setContestCurrentEntry,
  addOptimisticContestPick
} = slice.actions;

const toContestStatus = (
  status: string
): CONTEST_ENTRY_STATUS => {
  if (status in CONTEST_ENTRY_STATUS) {
    return status as CONTEST_ENTRY_STATUS;
  }
  throw new Error(`Invalid status: ${status}`);
};

export const getContestsState = (state: RootState): ContestsState => {
  return state.contests;
};

export const getNonDeletedContests = createSelector(
  [getContestsState],
  (contestsState): Contest[] => {
    return contestsState.contests.filter(contest => !contest.is_deleted);
  }
);

export const getContestIdByEntryId = (state: RootState, entryId: string) => {
  return getContestsState(state).entryIdToContestId[entryId];
};

export const getEntriesByContestId = (
  state: RootState,
  contestId: string | undefined
): ContestEntry[] | undefined => {
  return contestId
    ? getContestsState(state).contestIdToEntries[contestId]
    : undefined;
};

export const getContestById = (state: RootState, contestId: string | undefined) => {
  return getContestsState(state).contests.find((contest) => contest.id === contestId);
};

export const getContestEntryById = (state: RootState, contestEntryId: string | undefined): ContestEntry | undefined => {
  if (!contestEntryId) {
    return undefined;
  }
  const contestsState = getContestsState(state);
  const contestId = contestsState.entryIdToContestId[contestEntryId];
  if (!contestId) {
    return undefined;
  }
  const entries = contestsState.contestIdToEntries[contestId];
  if (!entries) {
    return undefined;
  }
  return entries.find(entry => entry.id === contestEntryId);
};

export const getFullContestEntryDetailsById = (state: RootState, contestEntryId: string | undefined): FullContestEntryDetails | undefined => {
  if (!contestEntryId) {
    return undefined;
  }
  return getContestsState(state).fullContestEntryDetails[contestEntryId];
};

export const getEntryRefreshLoadingState = (state: RootState, contestEntryId: string | undefined): LoadingState | undefined => {
  if (!contestEntryId) {
    return undefined;
  }
  return getContestsState(state).entryRefreshLoadingById[contestEntryId];
};

export const getCurrentEntryIdByContestId = (state: RootState, contestId: string | undefined): string | undefined => {
  if (!contestId) {
    return undefined;
  }
  return getContestsState(state).contestIdToCurrentEntryId[contestId];
};

export default slice.reducer;
